<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import Alert from '../components/Alert.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import Code from '../components/Code.svelte';
  import DistributionTable from '../components/DistributionTable.svelte';
  import PeriodChips from '../components/PeriodChips.svelte';
  import { getCatalog, getCubes } from '../lib/api.js';
  import { CUBE_STATES } from '../../../lib/cube_reconcile.js';
  import { HELP } from '../lib/help.js';

  const catalog = getCatalog();
  // Two independent reads: the private cubes are on this Pod and always
  // readable, the manifests depend on a partner grant. Failing the first
  // because the second is unreachable would hide the diagnostic exactly when
  // it is most wanted. The cubes promise is awaited in two places below —
  // created once here, so that is one fetch, not two.
  const cubes = getCubes();

  // Drill-down keys are scoped ("own:x" / "shared:x"), never the bare cube id:
  // a partner publishing a cube with the same name as ours is the normal case,
  // not a clash to be surprised by.
  let open = $state(new Set());
  function toggle(key) {
    const next = new Set(open);
    next.has(key) ? next.delete(key) : next.add(key);
    open = next;
  }

  // Per cube, because more than one can be open and each is being read on its
  // own terms.
  let periodBy = $state({});
  const pick = (key, period) => (periodBy = { ...periodBy, [key]: period });
  const periodsOf = (cube) => cube.periods.map((p) => p.period).filter(Boolean);

  // The cells to show for the chip that is selected. "All periods" sums each
  // group across the quarters — see the caveat rendered alongside it.
  function view(cube, selected) {
    if (selected !== 'all') {
      const p = cube.periods.find((x) => x.period === selected);
      return { rows: p?.cells ?? [], total: p?.total ?? 0 };
    }
    const byKey = new Map();
    for (const p of cube.periods) {
      for (const cell of p.cells) {
        const key = cell.values.join(' ');
        const seen = byKey.get(key);
        if (seen) seen.count += cell.count;
        else byKey.set(key, { values: cell.values, count: cell.count });
      }
    }
    const rows = [...byKey.values()].sort((a, b) => b.count - a.count);
    return { rows, total: rows.reduce((s, r) => s + r.count, 0) };
  }

  const genderDim = (ds) =>
    ds.find((d) => d.dataSet === 'genderNationalityCube')?.dimensions
      .find((dim) => /gender/i.test(dim));

  const publishedList = (to) => to.map((p) => p.label).join(', ');

  // The inbound cubes belonging to the partner this catalog view anchors on.
  // Matched by container URL — both sides get it from the same registry entry,
  // and a label is a display string, not a key.
  const sharedFor = (k, container) =>
    k.shared.find((s) => s.containers.includes(container)) ?? null;
</script>

<h1 class="wf2-title wf2-title-level-1">Catalog — your cubes and the shared ones</h1>
<p class="wf2-subtitle">What this Pod counted, who has a copy — and how two institutions can record the same thing in different words</p>

<Async promise={cubes} loading="Reading your private cubes…">
  {#snippet children(k)}
    {@const attention = k.cubes.filter((c) => CUBE_STATES[c.state]?.needsHuman)}

    <Section title="Your cubes — computed on this Pod">
      <Panel>
        <p class="wf2-text">
          These are the cubes in your <strong>private</strong> container
          — the direct output of Sources → Confirm. Nothing here has left the
          Pod by being here. Each row checks three things against each other:
          what you asked to be counted, what has actually been counted and
          written, and which partners hold a copy. When those three disagree,
          the row says so.
        </p>

        {#if attention.length}
          <Alert title="{attention.length} cube{attention.length === 1 ? '' : 's'} need{attention.length === 1 ? 's' : ''} a decision" variant="warning">
            <ul>
              {#each attention as c}
                <li>
                  <code class="mono">{c.id}</code> — {CUBE_STATES[c.state].note}
                </li>
              {/each}
            </ul>
          </Alert>
        {/if}

        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead>
              <tr>
                <th>Cube</th><th>Theme</th><th>Breaks down by</th><th>What it counts</th>
                <th>Groups</th><th>State</th><th>Shared with</th><th></th>
              </tr>
            </thead>
            <tbody>
              {#each k.cubes as c}
                {@const state = CUBE_STATES[c.state]}
                <tr>
                  <td class="mono">{c.id}</td>
                  <td>{#if c.theme}<span class="tag {c.theme}">{c.theme}</span>{:else}<span class="muted">—</span>{/if}</td>
                  <td class="mono">{c.dimensions.join(', ') || '—'}</td>
                  <td class="mono">{c.reducer} {c.measure}</td>
                  <td>{c.computed ? c.observations : '—'}</td>
                  <td>
                    <span class="tag cube-state {state.tone}" title={state.note}>{state.label}</span>
                  </td>
                  <td>{publishedList(c.publishedTo) || '—'}</td>
                  <td>
                    {#if c.observations}
                      <button class="link" onclick={() => toggle(`own:${c.id}`)}>
                        {open.has(`own:${c.id}`) ? 'Hide numbers' : 'Show numbers'}
                      </button>
                    {/if}
                  </td>
                </tr>
                {#if open.has(`own:${c.id}`)}
                  {@render drill({
                    key: `own:${c.id}`, cube: c, colspan: 8, measure: c.measure, note: state.note,
                    declared: c.declared ? c.declaredDimensions : null,
                    declaredBy: 'you asked it to count',
                  })}
                {/if}
              {/each}
            </tbody>
          </table>
        </div>

        <p class="wf2-caption">
          Container: <code class="mono">{k.container}</code> · groups smaller than
          {k.suppressBelow} were left out before the file was written.
          <strong>A group you don't see is a group you can't count</strong> — it
          may have been too small to include, or it may never have occurred, and
          nothing in the file can tell you which. Do not read a missing group as
          a zero.
        </p>
      </Panel>
    </Section>
  {/snippet}
</Async>

<Async promise={catalog} loading="Reading manifests…">
  {#snippet children(c)}
    {@const ownGender = genderDim(c.own.datasets)}
    {@const partnerGender = genderDim(c.partner.datasets)}

    <HelpDrawer {...HELP.catalog}>
      {#snippet children()}
        <p class="help-prose">A manifest dataset entry, e.g. the gender cube:</p>
        <Code lang="turtle" text={`<#genderNationalityCube> a void:Dataset, qb:DataSet ;
  dcterms:publisher "${c.own.publisher}" ;
  adr:theme "diversity" ;
  void:entities ${c.own.datasets.find((d) => d.dataSet === 'genderNationalityCube')?.entities ?? '…'} ;
  adr:measure "adr:artistCount" ;
  adr:dimension "${ownGender ?? 'adr:genderIdentity'}", "schema:nationality", "adr:period" .`} />
      {/snippet}
    </HelpDrawer>

    <Section title="What each institution says it shares">
      <Panel>
        <p class="wf2-text">
          Every institution publishes a short description of each cube it
          shares — what it is about, what it counts, how many figures are in it.
          Reading those is how you find out what is on offer without opening a
          single file.
        </p>
        {#if ownGender && partnerGender && ownGender !== partnerGender}
          <p class="wf2-hint">
            <strong>You and {c.partner.publisher} record gender differently</strong>
            — you use <code class="mono">{ownGender}</code>, they use
            <code class="mono">{partnerGender}</code>. Neither of you has to
            change: comparisons show both sets of figures side by side and leave
            the labels alone. See the “?” for why that is the point rather than a
            problem.
          </p>
        {/if}
      </Panel>
    </Section>

    <!-- Where our own cubes went is the "Published to" column above; a second
         table of the outbound copies said the same thing twice. What is left
         here is the direction the first table cannot see: what came in. -->
    <Section title="{c.partner.publisher} — shared with you">
      <Panel>
        {#if c.partner.status === 200}
          <Async promise={cubes} loading="Reading their cubes…">
            {#snippet children(k)}
              {@const bank = sharedFor(k, c.partner.container)}
              {@const listed = new Set(c.partner.datasets.map((d) => d.dataSet))}
              {@const unlisted = (bank?.cubes ?? []).filter((x) => !listed.has(x.id))}
              <div class="wf2-tablewrap" style="overflow-x:auto">
                <table class="wf2-table">
                  <thead>
                    <tr>
                      <th>Cube</th><th>Theme</th><th>Breaks down by</th>
                      <th>Groups</th><th>What it counts</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each c.partner.datasets as d}
                      {@const key = `shared:${d.dataSet}`}
                      {@const file = bank?.cubes.find((x) => x.id === d.dataSet) ?? null}
                      <tr>
                        <td class="mono">{d.dataSet}</td>
                        <td><span class="tag {d.theme}">{d.theme}</span></td>
                        <td class="mono">{d.dimensions.join(', ')}</td>
                        <td>
                          {d.entities}
                          {#if file && file.observations !== d.entities}
                            <span class="muted">({file.observations} in the file)</span>
                          {/if}
                        </td>
                        <td class="mono">{d.measure}</td>
                        <td>
                          {#if file?.observations}
                            <button class="link" onclick={() => toggle(key)}>
                              {open.has(key) ? 'Hide numbers' : 'Show numbers'}
                            </button>
                          {/if}
                        </td>
                      </tr>
                      {#if file && open.has(key)}
                        {@render drill({
                          key, cube: file, colspan: 6, measure: d.measure,
                          note: `${c.partner.publisher} counted these and decided what was too small to include, not you — this is the file exactly as it arrived.`,
                          // Their manifest lists the period among the dimensions;
                          // the reader classifies it as the period, so drop it
                          // before comparing or every cube looks divergent.
                          declared: d.dimensions.filter((x) => x !== k.period),
                          declaredBy: `${c.partner.publisher} said it counts`,
                        })}
                      {/if}
                    {/each}
                  </tbody>
                </table>
              </div>

              {#if unlisted.length}
                <p class="wf2-hint">
                  {unlisted.length} cube{unlisted.length === 1 ? '' : 's'} they share with you
                  {unlisted.length === 1 ? 'is' : 'are'} not in their own description of what
                  they share (<code class="mono">{unlisted.map((x) => x.id).join(', ')}</code>).
                  You can read {unlisted.length === 1 ? 'it' : 'them'}, but nobody has told you
                  what {unlisted.length === 1 ? 'it is' : 'they are'} — a description is a claim
                  about what was sent, and the sending can differ from the claim.
                </p>
              {/if}
            {/snippet}
          </Async>

          <p class="wf2-caption">Container: <code class="mono">{c.partner.container}</code></p>
          <p class="wf2-hint">
            Both institutions can exchange <span class="tag audience">audience</span>
            cubes using the same measure while retaining their own private records.
            The <span class="tag operations">operations</span> theme remains
            unilateral, and diversity axes may still use different vocabularies.
          </p>
        {:else}
          <p class="state error">⚠ Can't read this ({c.partner.status || 'no response'}) — they haven't shared anything with you yet.</p>
        {/if}
      </Panel>
    </Section>

    <div class="wf2-row" style="gap:12px">
      <Button variant="primary" to="analysis">View comparisons</Button>
      <!-- The other direction out of this screen: having seen which cubes exist
           and what each one carries, build a comparison from two of them. One
           button, not two — both used to land on the same screen. -->
      <Button variant="secondary" to="queries">Build a comparison</Button>
      <Button variant="secondary" to="dashboard">Back to dashboard</Button>
    </div>
  {/snippet}
</Async>

<!-- One cube's numbers, read the same way wherever they came from: quarter
     picker, one row per group, count and share. `declared` is the other half of
     the comparison — aggregations.json for our cubes, the partner's manifest for
     theirs — and is only rendered when it disagrees with the file. -->
{#snippet drill({ key, cube, colspan, measure, note, declared = null, declaredBy = '' })}
  {@const selected = periodBy[key] ?? 'all'}
  {@const shown = view(cube, selected)}
  <tr>
    <td {colspan} class="drill">
      {#if note}<p class="wf2-caption">{note}</p>{/if}
      {#if declared && declared.join() !== cube.dimensions.join()}
        <p class="wf2-hint">
          <strong>These don't match.</strong> The file actually counts
          <code class="mono">{cube.dimensions.join(', ')}</code>, but
          {declaredBy} <code class="mono">{declared.join(', ') || '—'}</code>.
          The numbers below are what the file really holds — trust those, and
          find out why the two disagree.
        </p>
      {/if}

      <PeriodChips periods={periodsOf(cube)} {selected} onselect={(p) => pick(key, p)} />

      <h4 class="wf2-title wf2-title-level-4">
        {selected === 'all' ? 'All periods' : selected} · {shown.total}
        <span class="wf2-caption">{measure}</span>
      </h4>
      <DistributionTable columns={cube.dimensions} rows={shown.rows} total={shown.total} />

      {#if selected === 'all' && cube.periods.length > 1}
        <p class="wf2-hint">
          Added up across {cube.periods.length} periods. A group left out of one
          quarter for being too small is missing from that quarter's total, so
          the real figure is <strong>this or higher</strong> — never lower. Pick
          a single quarter if you need a number you can stand behind.
        </p>
      {/if}
      {#if cube.dimensions.length > 1}
        <p class="wf2-hint">
          <strong>Careful adding these up.</strong> This cube counts
          {cube.dimensions.length} things in combination, and every combination
          too small to include was left out. So adding the rows together to get a
          total for just one of them gives you a number that is
          <strong>too low</strong>, and nothing on the page will warn you. For a
          total of one thing on its own, read the cube that counts that one thing
          on its own.
        </p>
      {/if}
    </td>
  </tr>
{/snippet}

<style>
  /* Three tones, because the six states are not six degrees of the same thing:
     four are normal outcomes, two mean someone has to go and look. */
  .cube-state.ok {
    background: color-mix(in srgb, var(--accent), transparent 82%);
    border-color: color-mix(in srgb, var(--accent), transparent 40%);
  }
  .cube-state.warn {
    background: color-mix(in srgb, var(--warn), transparent 82%);
    border-color: color-mix(in srgb, var(--warn), transparent 40%);
  }
  .cube-state.error {
    background: color-mix(in srgb, var(--danger), transparent 85%);
    border-color: color-mix(in srgb, var(--danger), transparent 40%);
    color: var(--danger);
  }
  .link {
    background: none; border: none; padding: 0;
    color: var(--accent); font: inherit; cursor: pointer; text-decoration: underline;
  }
  .drill { background: var(--surface-2); }
</style>
